import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Building2, PenLine, BadgeCheck, Percent, Euro, Coins, Gauge, Clock, Users2, Medal,
  FileSpreadsheet, FileText, Flag, Search, MapPin, Lock, UserSearch, Store, Table2, CircleDot,
} from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { usePost } from '@/lib/useData';
import { downloadPost } from '@/lib/download';
import { comparisonInfo } from '@/lib/periods';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { KpiCard } from '@/components/kpi/KpiCard';
import { CampaignCodeKpiTable } from '@/components/campaign/CampaignCodeKpiTable';
import { TrendComparisonNote } from '@/components/common/TrendComparisonNote';
import { GridSkeleton, CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { ScoreGauge } from '@/components/common/ScoreGauge';
import { StatusPill } from '@/components/common/StatusPill';
import { AgeDonut } from '@/components/charts/AgeDonut';
import { HorizontalBars } from '@/components/charts/HorizontalBars';
import { TrendAreaChart } from '@/components/charts/TrendAreaChart';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PerformanceBubble, CANAL_COLORS } from '@/components/charts/PerformanceBubble';
import { accentForFill } from '@/lib/colors';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

const spark = (series, key) => (series || []).map((s) => ({ x: s.period, y: s[key] }));

// "2026-S02" -> "S02"
function weekTick(p) {
  if (typeof p === 'string' && p.includes('-S')) return p.split('-')[1];
  return p;
}

// Renders the spot(s) a recruiter actually worked, primary first (by validated BS).
// At a glance: primary spot name inline; a hover card reveals the full ranked list.
function SpotsCell({ spots }) {
  const list = spots || [];
  if (list.length === 0) {
    return <span className="text-xs text-muted-foreground" data-testid="rd-spots-empty">—</span>;
  }
  const primary = list[0];
  const rest = list.slice(1);
  return (
    <div className="flex items-start gap-1.5" data-testid="rd-spots-cell">
      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-text" />
      <div className="min-w-0">
        <div className="break-words text-xs font-medium text-foreground" data-testid="rd-spot-primary">
          {primary.lieu}
          {primary.bs > 0 && (
            <span className="ml-1 font-normal tabular text-muted-foreground">· {fmtInt(primary.bs)} BS</span>
          )}
        </div>
        {rest.length > 0 && (
          <HoverCard openDelay={80} closeDelay={80}>
            <HoverCardTrigger asChild>
              <button
                type="button"
                className="mt-1 inline-flex items-center rounded-md border border-dashed px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground transition-colors duration-150 hover:border-solid hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="rd-spots-more"
              >
                +{rest.length} autre{rest.length > 1 ? 's' : ''} spot{rest.length > 1 ? 's' : ''}
              </button>
            </HoverCardTrigger>
            <HoverCardContent align="start" className="w-72 p-0">
              <div className="border-b px-3 py-2">
                <p className="text-xs font-semibold text-foreground">Tous les spots ({list.length})</p>
                <p className="text-[10px] text-muted-foreground">Classés par BS validés</p>
              </div>
              <ScrollArea className="max-h-64">
                <ul className="divide-y">
                  {list.map((s, idx) => (
                    <li key={`${s.lieu}-${idx}`} className="flex items-start justify-between gap-3 px-3 py-1.5">
                      <span className="break-words text-xs text-foreground">
                        <span className="mr-1 text-muted-foreground">{idx + 1}.</span>{s.lieu}
                      </span>
                      <span className="shrink-0 tabular text-[11px] text-muted-foreground">{fmtInt(s.bs)} BS</span>
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            </HoverCardContent>
          </HoverCard>
        )}
      </div>
    </div>
  );
}

const RANK_METRICS = {
  bs_reel: { label: 'BS validés', fmt: (v) => fmtInt(v) },
  bs_rue: { label: 'BS signés', fmt: (v) => fmtInt(v) },
  montant: { label: 'Montant collecté', fmt: (v) => fmtEur(v, 0) },
  taux_transfo: { label: 'Taux de transformation', fmt: (v) => fmtPct(v) },
  don_moyen: { label: 'Don moyen', fmt: (v) => fmtEur(v, 2) },
  productivite: { label: 'Productivité', fmt: (v) => `${fmtNum(v, 2)} BS/h` },
  age_moyen: { label: 'Âge moyen', fmt: (v) => `${fmtNum(v, 1)} ans` },
};

// Sort options for the internal per-RD and per-lieu breakdown tables.
const RD_SORTS = [
  { value: 'bs_reel', label: 'BS validés' },
  { value: 'bs_rue', label: 'BS signés' },
  { value: 'montant', label: 'Montant collecté' },
  { value: 'taux_transfo', label: 'Transformation' },
  { value: 'don_moyen', label: 'Don moyen' },
  { value: 'age_moyen', label: 'Âge moyen' },
  { value: 'nb_spots', label: 'Nb de spots' },
];
const LIEU_SORTS = [
  { value: 'total_bs_reel', label: 'BS validés' },
  { value: 'total_bs_rue', label: 'BS signés' },
  { value: 'montant', label: 'Montant collecté' },
  { value: 'taux_transfo', label: 'Transformation' },
  { value: 'don_moyen', label: 'Don moyen' },
  { value: 'age_moyen', label: 'Âge moyen' },
  { value: 'days', label: 'Jours exploités' },
];

const VIEW_OPTIONS = [
  { v: 'table', label: 'Tableau', icon: Table2 },
  { v: 'chart', label: 'Graphique', icon: CircleDot },
];

const ViewToggle = ({ value, onChange, testid }) => (
  <div className="inline-flex rounded-xl border bg-card p-0.5" data-testid={testid}>
    {VIEW_OPTIONS.map(({ v, label, icon: Icon }) => {
      const active = value === v;
      return (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          aria-pressed={active}
          data-testid={`${testid}-${v}`}
          className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-[background-color,color] duration-200 focus-visible:outline-none ${active ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
          style={active ? { background: 'var(--tenant-accent-500)' } : undefined}
        >
          <Icon className="h-3.5 w-3.5" />{label}
        </button>
      );
    })}
  </div>
);

export default function Consolidation() {
  const { body, periodKey, period, comparePeriod } = useFilters();
  const [metric, setMetric] = useState('bs_reel');
  const [trendMode, setTrendMode] = useState('global');
  const [exporting, setExporting] = useState(null);
  const { data, isLoading, isError, error, refetch } = usePost('/admin/analytics/consolidated', body, true,
    { refetchInterval: 60_000, staleTime: 30_000 });
  const isForbidden = error?.response?.status === 403;

  // Internal (admin-only) breakdowns for the selected period.
  const [rdMetric, setRdMetric] = useState('bs_reel');
  const [rdSearch, setRdSearch] = useState('');
  const [rdLimit, setRdLimit] = useState(25);
  const [rdView, setRdView] = useState('table');
  const [lieuMetric, setLieuMetric] = useState('total_bs_reel');
  const [lieuSearch, setLieuSearch] = useState('');
  const [lieuLimit, setLieuLimit] = useState(25);
  const [lieuView, setLieuView] = useState('table');
  const { data: rdData, isLoading: rdLoading } = usePost('/admin/analytics/consolidated/recruiters', body);
  const { data: lieuData, isLoading: lieuLoading } = usePost('/admin/analytics/consolidated/lieux', body);

  const rdRows = useMemo(() => {
    const list = (rdData?.recruiters || []).slice();
    const q = rdSearch.trim().toLowerCase();
    const f = q ? list.filter((r) => r.key.toLowerCase().includes(q) || (r.code || '').toLowerCase().includes(q)) : list;
    f.sort((a, b) => (b[rdMetric] ?? 0) - (a[rdMetric] ?? 0));
    return f;
  }, [rdData, rdSearch, rdMetric]);
  const rdShown = rdRows.slice(0, rdLimit);
  // Chart dataset: top producers by volume (BS validés), capped to keep it legible.
  const rdChart = useMemo(() => (
    [...rdRows].sort((a, b) => (b.bs_reel ?? 0) - (a.bs_reel ?? 0)).slice(0, 60)
      .map((r) => ({ ...r, _sub: (r.association_names || []).join(' · ') }))
  ), [rdRows]);

  const lieuRows = useMemo(() => {
    const list = (lieuData?.spots || []).slice();
    const q = lieuSearch.trim().toLowerCase();
    const f = q ? list.filter((s) => (s.lieu || '').toLowerCase().includes(q) || (s.region || '').toLowerCase().includes(q)) : list;
    f.sort((a, b) => (b[lieuMetric] ?? 0) - (a[lieuMetric] ?? 0));
    return f;
  }, [lieuData, lieuSearch, lieuMetric]);
  const lieuShown = lieuRows.slice(0, lieuLimit);
  const lieuChart = useMemo(() => (
    [...lieuRows].sort((a, b) => (b.total_bs_reel ?? 0) - (a.total_bs_reel ?? 0)).slice(0, 60)
      .map((s) => ({ ...s, _sub: `${s.canal} · ${s.region}` }))
  ), [lieuRows]);

  const kpis = data?.kpis;
  const series = data?.series || [];
  const seriesAssoc = data?.series_assoc || [];
  const assocMeta = data?.assoc_meta || [];
  const ranking = data?.ranking || [];
  const obj = data?.objective;
  const topCodes = data?.top_codes || [];
  const codeBreakdown = data?.code_breakdown || [];
  const cmp = useMemo(() => comparisonInfo(periodKey, period, comparePeriod), [periodKey, period, comparePeriod]);

  const hasData = !!data && data.record_count > 0;

  const runExport = async (kind) => {
    setExporting(kind);
    try {
      const ext = kind === 'excel' ? 'xlsx' : 'pdf';
      await downloadPost(
        `/admin/analytics/consolidated/${kind}`,
        body,
        `SHARITI_consolidation_${new Date().toISOString().slice(0, 10)}.${ext}`,
      );
      toast.success('Export généré');
    } catch {
      toast.error('Export impossible');
    } finally {
      setExporting(null);
    }
  };

  const cards = useMemo(() => {
    if (!kpis) return [];
    return [
      { label: 'BS signés', value: fmtInt(kpis.bs_rue.value), icon: PenLine, trend: kpis.bs_rue.pct_change, spark: spark(series, 'bs_rue') },
      { label: 'BS validés', value: fmtInt(kpis.bs_reel.value), icon: BadgeCheck, trend: kpis.bs_reel.pct_change, spark: spark(series, 'bs_reel'), accent: true },
      { label: 'Taux de transformation', value: fmtPct(kpis.taux_transfo.value), icon: Percent, trend: kpis.taux_transfo.pct_change, spark: spark(series, 'taux_transfo') },
      { label: 'Don moyen', value: fmtEur(kpis.don_moyen.value), icon: Euro, trend: kpis.don_moyen.pct_change, spark: spark(series, 'don_moyen') },
      { label: 'Montant collecté', value: fmtEur(kpis.montant.value, 0), icon: Coins, trend: kpis.montant.pct_change, spark: spark(series, 'montant') },
      { label: 'Productivité horaire', value: `${fmtNum(kpis.productivite.value, 2)} BS/h`, icon: Gauge, trend: kpis.productivite.pct_change, spark: spark(series, 'productivite') },
      { label: 'Heures réalisées', value: fmtInt(kpis.heures.value), icon: Clock, trend: kpis.heures.pct_change, spark: spark(series, 'heures') },
      { label: 'Âge moyen', value: `${fmtNum(kpis.age_moyen.value, 1)} ans`, icon: Users2, trend: kpis.age_moyen.pct_change },
    ];
  }, [kpis, series]);

  const barData = useMemo(
    () => ranking.map((r) => ({
      key: r.short_name,
      value: r[metric],
      color: accentForFill(r.primary_color || '#EE722E'),
    })),
    [ranking, metric]
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vue consolidée"
        icon={Building2}
        subtitle={`Toutes les associations${data ? ` · ${data.association_count} associations` : ''} · pilotage global SHARITI`}
        actions={
          <>
            <Button
              variant="outline"
              className="gap-2 rounded-xl"
              onClick={() => runExport('excel')}
              disabled={!hasData || exporting !== null}
              data-testid="consolidated-export-excel">
              <FileSpreadsheet className="h-4 w-4" />
              {exporting === 'excel' ? 'Génération…' : 'Excel'}
            </Button>
            <Button
              className="gap-2 rounded-xl text-white"
              style={{ background: 'var(--tenant-accent-500)' }}
              onClick={() => runExport('pdf')}
              disabled={!hasData || exporting !== null}
              data-testid="consolidated-export-pdf">
              <FileText className="h-4 w-4" />
              {exporting === 'pdf' ? 'Génération…' : 'PDF'}
            </Button>
          </>
        }
      />

      {isForbidden ? (
        <EmptyState title="Accès restreint" body="Cette vue est réservée aux administrateurs SHARITI." />
      ) : isError ? (
        <ErrorState
          title="Impossible de charger la vue consolidée"
          body="Le calcul consolidé a échoué ou expiré. Réessayez dans un instant."
          onRetry={refetch}
        />
      ) : isLoading ? (
        <>
          <GridSkeleton count={8} />
          <CardSkeleton />
        </>
      ) : !data || data.record_count === 0 ? (
        <EmptyState title="Aucune donnée" body="Aucune donnée consolidée sur la période sélectionnée." />
      ) : (
        <>
          {/* Global KPI grid */}
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-foreground">Indicateurs consolidés</h2>
              <TrendComparisonNote />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="consolidated-kpis">
              {cards.map((c, i) => <KpiCard key={i} {...c} trendTooltip={cmp.full} />)}
            </div>
          </div>

          {/* Objectif global + global score */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <SectionCard
              title="Pilotage par objectif global"
              subtitle="Cumul des objectifs annuels de toutes les associations"
              className="xl:col-span-2"
              action={obj && <StatusPill status={obj.status} />}
              testid="global-objective-card">
              {obj ? (
                <div className="space-y-5">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <span className="font-display text-4xl font-semibold tabular" data-testid="global-objective-pct">{fmtPct(obj.pct)}</span>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {fmtInt(obj.achieved)} / {fmtInt(obj.target)} BS validés · reste {fmtInt(obj.remaining)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 rounded-xl accent-bg-soft px-3 py-2 text-sm">
                      <Flag className="h-4 w-4 accent-text" />
                      <span className="font-medium">
                        {obj.status === 'atteint' ? 'Objectifs globaux tenus.' : obj.status === 'attention' ? 'Vigilance : rythme à maintenir.' : 'Retard global à rattraper.'}
                      </span>
                    </div>
                  </div>
                  <Progress value={Math.min(obj.pct * 100, 100)} className="h-2.5" />
                  <div className="space-y-3 pt-1">
                    {obj.contributions.map((c) => (
                      <div key={c.code} data-testid={`objective-contrib-${c.code}`} className={c.active === false ? 'opacity-60' : ''}>
                        <div className="mb-1 flex items-center justify-between text-xs">
                          <span className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.primary_color || '#EE722E' }} />
                            <span className="font-medium text-foreground">{c.short_name}</span>
                          </span>
                          {c.active === false ? (
                            <Badge variant="outline" className="rounded-full text-[10px] font-medium text-muted-foreground" data-testid={`objective-inactive-${c.code}`}>
                              Non actif cette année · hors objectif global
                            </Badge>
                          ) : (
                            <span className="tabular text-muted-foreground">
                              {fmtInt(c.achieved)} / {fmtInt(c.target)} · <span className="font-semibold text-foreground">{fmtPct(c.pct)}</span>
                            </span>
                          )}
                        </div>
                        {c.active === false ? (
                          <div className="h-1.5 rounded-full bg-muted" />
                        ) : (
                          <Progress value={Math.min(c.pct * 100, 100)} className="h-1.5" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="py-8 text-center text-sm text-muted-foreground">Aucun objectif annuel défini.</p>
              )}
            </SectionCard>

            <SectionCard title="Score global" subtitle="Toutes associations confondues">
              {data?.score ? (
                <div className="flex flex-col items-center py-2">
                  <ScoreGauge score={data.score.score} rating={data.score.rating} />
                  <div className="mt-4 grid w-full grid-cols-2 gap-2 text-center">
                    {Object.entries(data.score.components || {}).map(([k, v]) => (
                      <div key={k} className="rounded-xl bg-muted/50 px-2 py-2">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{labelComp(k)}</p>
                        <p className="font-display text-sm font-semibold tabular">{v}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <div className="h-40" />}
            </SectionCard>
          </div>

          {/* Consolidated trend (global / per-association toggle) + age */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <SectionCard
              title="Évolution des signatures"
              subtitle={trendMode === 'global' ? 'BS signés vs BS validés · toutes associations' : 'BS validés · par association'}
              className="xl:col-span-2"
              action={
                <div className="inline-flex rounded-lg border bg-card p-0.5" data-testid="trend-mode-toggle">
                  <button
                    onClick={() => setTrendMode('global')}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${trendMode === 'global' ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
                    style={trendMode === 'global' ? { background: 'var(--tenant-accent-500)' } : {}}
                    data-testid="trend-mode-global">Global</button>
                  <button
                    onClick={() => setTrendMode('assoc')}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${trendMode === 'assoc' ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
                    style={trendMode === 'assoc' ? { background: 'var(--tenant-accent-500)' } : {}}
                    data-testid="trend-mode-assoc">Par association</button>
                </div>
              }
              testid="consolidated-trend-card">
              {series.length ? (
                trendMode === 'global' ? (
                  <TrendAreaChart data={series} xTickFormatter={weekTick}
                    formatTip={(v) => fmtInt(v)} formatY={(v) => fmtInt(v)}
                    series={[
                      { key: 'bs_rue', name: 'BS signés', color: '#5B6472' },
                      { key: 'bs_reel', name: 'BS validés', color: 'var(--tenant-accent-500)' },
                    ]} />
                ) : (
                  <TrendAreaChart data={seriesAssoc} xTickFormatter={weekTick}
                    formatTip={(v) => fmtInt(v)} formatY={(v) => fmtInt(v)}
                    series={assocMeta.map((a) => ({ key: a.code, name: a.short_name, color: accentForFill(a.primary_color || '#EE722E') }))} />
                )
              ) : <EmptyState body="Aucune donnée sur la période." />}
            </SectionCard>

            <SectionCard title="Répartition par âge" subtitle="Profil global des donateurs">
              {data?.age_distribution?.some((a) => a.count > 0)
                ? <AgeDonut data={data.age_distribution} />
                : <EmptyState body="Aucune donnée d'âge sur la période." />}
            </SectionCard>
          </div>

          {/* Ranking table (full width) */}
          <SectionCard
            title="Classement des associations"
            subtitle="Classées par BS validés sur la période"
            bodyClassName="pt-0"
            testid="ranking-card">
            <div className="overflow-hidden rounded-xl border">
              <Table data-testid="ranking-table">
                <TableHeader>
                  <TableRow className="bg-muted/60 hover:bg-muted/60">
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Association</TableHead>
                    <TableHead className="text-right">BS signés</TableHead>
                    <TableHead className="text-right">BS validés</TableHead>
                    <TableHead className="text-right">Transfo</TableHead>
                    <TableHead className="text-right">Don moyen</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead className="text-right">Âge moyen</TableHead>
                    <TableHead className="text-right">Prod.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ranking.map((r, i) => (
                    <TableRow key={r.code} data-testid={`ranking-row-${r.code}`}>
                      <TableCell>
                        {i < 3 ? (
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full"
                            style={{ background: 'var(--tenant-accent-100)' }}>
                            <Medal className="h-3.5 w-3.5 accent-text" />
                          </span>
                        ) : (
                          <span className="pl-1.5 text-sm text-muted-foreground tabular">{i + 1}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: r.primary_color || '#EE722E' }} />
                          <span className="font-medium">{r.short_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular">{fmtInt(r.bs_rue)}</TableCell>
                      <TableCell className="text-right tabular font-semibold">{fmtInt(r.bs_reel)}</TableCell>
                      <TableCell className="text-right tabular">{fmtPct(r.taux_transfo)}</TableCell>
                      <TableCell className="text-right tabular">{fmtEur(r.don_moyen, 2)}</TableCell>
                      <TableCell className="text-right tabular">{fmtEur(r.montant, 0)}</TableCell>
                      <TableCell className="text-right tabular" data-testid={`ranking-age-${r.code}`}>{fmtNum(r.age_moyen, 1)} ans</TableCell>
                      <TableCell className="text-right tabular">{fmtNum(r.productivite, 2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </SectionCard>

          {/* Inter-association comparison + top campaign codes */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <SectionCard
              title="Comparaison inter-associations"
              subtitle={RANK_METRICS[metric].label}
              action={
                <Select value={metric} onValueChange={setMetric}>
                  <SelectTrigger className="h-9 w-[190px] rounded-xl bg-card" data-testid="ranking-metric-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(RANK_METRICS).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              }
              testid="ranking-chart-card">
              <HorizontalBars
                data={barData}
                height={Math.max(220, ranking.length * 52)}
                formatTip={(v) => RANK_METRICS[metric].fmt(v)}
                highlightTop={false}
              />
            </SectionCard>

            <SectionCard title="Meilleurs codes campagne"
              subtitle="Classés par BS validés · toutes associations"
              testid="top-codes-card">
              {topCodes.length ? (
                <HorizontalBars
                  data={topCodes.map((t) => ({ key: `${t.key} · ${t.association || '—'}`, value: t.bs_reel, color: accentForFill(t.color || '#EE722E') }))}
                  subLabels={Object.fromEntries(topCodes.map((t) => [`${t.key} · ${t.association || '—'}`, [t.canal, t.region].filter(Boolean)]))}
                  yWidth={160}
                  height={Math.max(240, topCodes.length * 58)}
                  formatTip={(v) => `${fmtInt(v)} BS validés`}
                  highlightTop={false}
                />
              ) : <EmptyState body="Aucun code campagne sur la période." />}
            </SectionCard>
          </div>

          {/* INTERNAL (admin) — per-RD breakdown for the selected period */}
          <SectionCard
            title="Détail par recruteur (RD)"
            subtitle="Qui a produit sur la période — tous les indicateurs · interne SHARITI"
            bodyClassName="pt-0"
            testid="consolidated-rd-card"
            action={
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input value={rdSearch} onChange={(e) => setRdSearch(e.target.value)} placeholder="Recruteur…"
                    className="h-9 w-40 rounded-xl pl-8" data-testid="rd-search" />
                </div>
                {rdView === 'table' && (
                  <Select value={rdMetric} onValueChange={(v) => { setRdMetric(v); setRdLimit(25); }}>
                    <SelectTrigger className="h-9 w-[170px] rounded-xl bg-card" data-testid="rd-sort"><SelectValue /></SelectTrigger>
                    <SelectContent>{RD_SORTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                <ViewToggle value={rdView} onChange={setRdView} testid="rd-view" />
              </div>
            }>
            <div className="mb-3 flex items-center gap-2 rounded-xl border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground" data-testid="rd-internal-note">
              <Lock className="h-3.5 w-3.5 shrink-0 accent-text" />
              <span>Vue <strong className="text-foreground">interne SHARITI</strong> — performance individuelle des recruteurs, jamais exposée côté association.</span>
            </div>
            {rdLoading ? (
              <CardSkeleton height="h-64" />
            ) : rdRows.length === 0 ? (
              <EmptyState body="Aucun recruteur sur la période sélectionnée." />
            ) : rdView === 'chart' ? (
              <>
                <PerformanceBubble
                  data={rdChart}
                  xKey="bs_reel" yKey="taux_transfo" zKey="montant"
                  nameKey="key" subKey="_sub" groupKey="canal_principal"
                  xLabel="BS validés (volume)" yLabel="Taux de transformation"
                  formatX={(v) => fmtInt(v)} formatY={(v) => fmtPct(v)} formatZ={(v) => fmtEur(v, 0)}
                  extraFields={[
                    { key: 'don_moyen', label: 'Don moyen', format: (v) => fmtEur(v, 2) },
                    { key: 'age_moyen', label: 'Âge moyen', format: (v) => `${fmtNum(v, 1)} ans` },
                    { key: 'nb_spots', label: 'Spots', format: (v) => fmtInt(v) },
                  ]}
                />
                <p className="mt-1 text-xs text-muted-foreground" data-testid="rd-chart-note">
                  Top {rdChart.length} recruteurs par volume · haut-droite = fort volume + forte transformation (les « stars »)
                </p>
              </>
            ) : (
              <>
                <div className="overflow-x-auto rounded-xl border">
                  <Table data-testid="rd-table">
                    <TableHeader>
                      <TableRow className="bg-muted/60 hover:bg-muted/60 [&>th]:whitespace-nowrap">
                        <TableHead className="w-10">#</TableHead>
                        <TableHead className="min-w-[150px]">Recruteur</TableHead>
                        <TableHead className="text-right">BS signés</TableHead>
                        <TableHead className="text-right">BS validés</TableHead>
                        <TableHead className="text-right">Transfo</TableHead>
                        <TableHead className="text-right">Don moyen</TableHead>
                        <TableHead className="text-right">Âge moyen</TableHead>
                        <TableHead className="text-right">Montant</TableHead>
                        <TableHead className="min-w-[170px]">Spot(s)</TableHead>
                        <TableHead>Canal principal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rdShown.map((r, i) => (
                        <TableRow key={r.key} data-testid="rd-row">
                          <TableCell className="tabular text-muted-foreground align-top">{i + 1}</TableCell>
                          <TableCell className="align-top">
                            <div className="break-words font-medium text-foreground">{r.key}</div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-1">
                              {r.code && <span className="font-mono text-[10px] text-muted-foreground">{r.code}</span>}
                              {(r.association_names || []).map((a) => (
                                <span key={a} className="rounded bg-muted px-1 text-[10px] text-muted-foreground">{a}</span>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-right tabular whitespace-nowrap align-top">{fmtInt(r.bs_rue)}</TableCell>
                          <TableCell className="text-right tabular font-semibold whitespace-nowrap align-top">{fmtInt(r.bs_reel)}</TableCell>
                          <TableCell className="text-right tabular whitespace-nowrap align-top">{fmtPct(r.taux_transfo)}</TableCell>
                          <TableCell className="text-right tabular whitespace-nowrap align-top">{fmtEur(r.don_moyen, 2)}</TableCell>
                          <TableCell className="text-right tabular whitespace-nowrap align-top">{fmtNum(r.age_moyen, 1)} ans</TableCell>
                          <TableCell className="text-right tabular whitespace-nowrap align-top">{fmtEur(r.montant, 0)}</TableCell>
                          <TableCell className="min-w-[170px] max-w-[260px] align-top"><SpotsCell spots={r.spots} /></TableCell>
                          <TableCell className="break-words text-xs text-muted-foreground align-top">{r.canal_principal || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="mt-2 text-xs text-muted-foreground" data-testid="rd-count">
                  {rdShown.length} / {rdRows.length} recruteur(s) · {fmtInt(rdData?.record_count || 0)} bulletins sur la période
                </p>
                {rdShown.length < rdRows.length && (
                  <div className="mt-2 flex justify-center">
                    <Button variant="outline" className="rounded-xl" onClick={() => setRdLimit((l) => l + 25)} data-testid="rd-load-more">
                      Afficher plus ({rdRows.length - rdShown.length})
                    </Button>
                  </div>
                )}
              </>
            )}
          </SectionCard>

          {/* INTERNAL (admin) — per-lieu breakdown for the selected period */}
          <SectionCard
            title="Détail par lieu"
            subtitle="Où l'on a produit sur la période — villes & spots exacts, tous les indicateurs · interne SHARITI"
            bodyClassName="pt-0"
            testid="consolidated-lieu-card"
            action={
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input value={lieuSearch} onChange={(e) => setLieuSearch(e.target.value)} placeholder="Ville / spot…"
                    className="h-9 w-40 rounded-xl pl-8" data-testid="lieu-search" />
                </div>
                {lieuView === 'table' && (
                  <Select value={lieuMetric} onValueChange={(v) => { setLieuMetric(v); setLieuLimit(25); }}>
                    <SelectTrigger className="h-9 w-[170px] rounded-xl bg-card" data-testid="lieu-sort"><SelectValue /></SelectTrigger>
                    <SelectContent>{LIEU_SORTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                <ViewToggle value={lieuView} onChange={setLieuView} testid="lieu-view" />
              </div>
            }>
            {lieuLoading ? (
              <CardSkeleton height="h-64" />
            ) : lieuRows.length === 0 ? (
              <EmptyState body="Aucun lieu sur la période sélectionnée." />
            ) : lieuView === 'chart' ? (
              <>
                <PerformanceBubble
                  data={lieuChart}
                  xKey="total_bs_reel" yKey="taux_transfo" zKey="montant"
                  nameKey="lieu" subKey="_sub" groupKey="canal"
                  xLabel="BS validés (volume)" yLabel="Taux de transformation"
                  formatX={(v) => fmtInt(v)} formatY={(v) => fmtPct(v)} formatZ={(v) => fmtEur(v, 0)}
                  extraFields={[
                    { key: 'don_moyen', label: 'Don moyen', format: (v) => fmtEur(v, 2) },
                    { key: 'age_moyen', label: 'Âge moyen', format: (v) => `${fmtNum(v, 1)} ans` },
                    { key: 'days', label: 'Jours', format: (v) => fmtInt(v) },
                  ]}
                />
                <p className="mt-1 text-xs text-muted-foreground" data-testid="lieu-chart-note">
                  Top {lieuChart.length} lieux par volume · haut-droite = fort volume + forte transformation
                </p>
              </>
            ) : (
              <>
                <div className="overflow-x-auto rounded-xl border">
                  <Table data-testid="lieu-table">
                    <TableHeader>
                      <TableRow className="bg-muted/60 hover:bg-muted/60">
                        <TableHead className="w-10">#</TableHead>
                        <TableHead>Lieu</TableHead>
                        <TableHead className="text-right">BS signés</TableHead>
                        <TableHead className="text-right">BS validés</TableHead>
                        <TableHead className="text-right">Transfo</TableHead>
                        <TableHead className="text-right">Don moyen</TableHead>
                        <TableHead className="text-right">Âge moyen</TableHead>
                        <TableHead className="text-right">Montant</TableHead>
                        <TableHead className="text-right">Jours</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lieuShown.map((s, i) => (
                        <TableRow key={s.lieu} data-testid="lieu-row">
                          <TableCell className="tabular text-muted-foreground">{i + 1}</TableCell>
                          <TableCell>
                            <div className="flex items-start gap-1.5">
                              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              <div className="min-w-0">
                                <div className="break-words font-medium text-foreground">{s.lieu}</div>
                                <div className="mt-0.5 flex flex-wrap items-center gap-1">
                                  <span className="inline-flex items-center gap-0.5 rounded bg-muted px-1 text-[10px] text-muted-foreground">
                                    <Store className="h-2.5 w-2.5" />{s.canal}
                                  </span>
                                  <span className="text-[10px] text-muted-foreground break-words">· {s.region}</span>
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right tabular">{fmtInt(s.total_bs_rue)}</TableCell>
                          <TableCell className="text-right tabular font-semibold">{fmtInt(s.total_bs_reel)}</TableCell>
                          <TableCell className="text-right tabular">{fmtPct(s.taux_transfo)}</TableCell>
                          <TableCell className="text-right tabular">{fmtEur(s.don_moyen, 2)}</TableCell>
                          <TableCell className="text-right tabular">{fmtNum(s.age_moyen, 1)} ans</TableCell>
                          <TableCell className="text-right tabular">{fmtEur(s.montant, 0)}</TableCell>
                          <TableCell className="text-right tabular">{fmtInt(s.days)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="mt-2 text-xs text-muted-foreground" data-testid="lieu-count">
                  {lieuShown.length} / {lieuRows.length} lieu(x) sur la période
                </p>
                {lieuShown.length < lieuRows.length && (
                  <div className="mt-2 flex justify-center">
                    <Button variant="outline" className="rounded-xl" onClick={() => setLieuLimit((l) => l + 25)} data-testid="lieu-load-more">
                      Afficher plus ({lieuRows.length - lieuShown.length})
                    </Button>
                  </div>
                )}
              </>
            )}
          </SectionCard>

          {/* Detailed per-campaign-code KPI table (age moyen & don moyen highlighted) */}
          {codeBreakdown.length ? (
            <CampaignCodeKpiTable
              items={codeBreakdown}
              showAssociation
              title="Détail par code campagne"
              subtitle={`Âge moyen & don moyen — ${codeBreakdown.length} codes · toutes associations`}
              testid="consolidated-code-kpi-table"
            />
          ) : null}
        </>
      )}
    </div>
  );
}

function labelComp(k) {
  return { transformation: 'Transfo', productivite: 'Productiv.', don_moyen: 'Don moyen', tendance: 'Tendance' }[k] || k;
}
